// THE MANIFEST → RECORDS GRANT RULE on the client (a permission rule).
//
// A module app's manifest declares its record collections (`collections[]`);
// an install turns them into `capabilities.records.collections` — the grant
// the server's AccessResolver gates every record read and write on. The SERVER
// install path owns this rule (server src/domains/workspaces/services/
// WorkspaceAppManifest.js: normalizeCollectionAccess + deriveFieldRules +
// deriveCollectionsCapability). The shell's module assign and "Check for
// updates" build the same grant on the client (workspaceModuleRef.js), and until
// 2026-09-30 they ignored `access`: a declared `access: 'read'` became a plain
// key — member read AND editor WRITE — where the server grants
// { read: 'member', write: 'admin' } (crm_settings was writable by editors after
// a "Check for updates"; found by crm-w1). This is the client twin of the
// server rule, case for case:
//   access absent / null / 'own'  → the plain key (member read, editor write)
//   'read'                        → { key, read: 'member', write: 'admin' }
//   'own-admin'                   → { key, read: 'member', write: 'admin' } —
//     the app OWNS the collection, members read, admins write (a settings
//     collection; decided 2026-09-30). The same grant as 'read'; the
//     dependency graph (appDependencyGraph.js) counts it as the owner.
//   'own-rows'                    → { key, read: 'member', write: 'own' }
//   'private-rows'                → { key, read: 'own', write: 'own' }
//   anything else                 → as 'read' (fail CLOSED)
//   `fields` (per-field rules)    → the object entry: the levels above (a plain
//     key's are member / editor) plus the valid rules. A malformed rule is
//     dropped (read: member | own | admin | perm:<key>; write: editor | admin |
//     server | perm:<key>); the server's resolver checks a perm key against its
//     catalog and fails closed.
//   `teams` (per-team overrides) → the object entry carries the map — the
//     server's deriveTeamOverrides (server 121a14c2b, 2026-10-05): slugs
//     trimmed and lower-cased (the resolver's keying), an unknown level fails
//     CLOSED to 'admin', a team entry with no axis is dropped, nothing usable
//     → no map. The DFI Finances app declares its fin_* collections
//     `{ read: 'admin', teams: { 'dfli-costs': { read: 'member' } } }`; before
//     this the client grant dropped the exception (admin-only).
// The key is trimmed; an entry without one is dropped.
//
// An entry with NO `access` (absent, null or '') that declares its own `read`
// / `write` levels keeps them ({ key, read, write }, bellforge's manifests),
// exactly as the server: a level the resolver does not know fails CLOSED to
// 'admin', an undeclared axis stays absent, a 'scoped' level carries its
// `scopeField`. Every valid read tier is at most `member` and every write
// tier at most `editor` — the plain key's levels.
//
// ONE copy on the client: workspaceModuleRef.js imports this for both of its
// manifest reads. Self-contained body (frank serializes functions; a
// module-scope read inside the body would not survive it).
export const collectionGrantsOf = (manifestCollections) => {
  const list = Array.isArray(manifestCollections) ? manifestCollections : []
  const FIELD_READ = ['member', 'own', 'admin']
  const FIELD_WRITE = ['editor', 'admin', 'server']
  // The resolver's record grant levels (server core/constants/recordGrantLevels.js).
  // 'acl' is a write level too (server G-1, 98a5bc4e2): a row's ACL decides who
  // may edit it — without it a declared write 'acl' failed CLOSED to 'admin'.
  const LEVEL_READ = ['member', 'admin', 'own', 'scoped', 'acl']
  const LEVEL_WRITE = ['editor', 'admin', 'own', 'scoped', 'acl']
  const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/
  // A declared level: present and non-empty (a falsy level reads as absent).
  const declared = (v) => v !== undefined && v !== null && v !== ''
  const tierOk = (v, tiers) =>
    typeof v === 'string' && (v.startsWith('perm:') ? v.length > 5 : tiers.includes(v))
  const fieldRules = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
    const rules = {}
    for (const name of Object.keys(raw)) {
      const rule = raw[name]
      if (!FIELD_NAME.test(name) || !rule || typeof rule !== 'object' || Array.isArray(rule)) {
        continue
      }
      const r = {}
      if (rule.read !== undefined) {
        if (!tierOk(rule.read, FIELD_READ)) continue
        r.read = rule.read
      }
      if (rule.write !== undefined) {
        if (!tierOk(rule.write, FIELD_WRITE)) continue
        r.write = rule.write
      }
      if (Object.keys(r).length) rules[name] = r
    }
    return Object.keys(rules).length ? rules : null
  }
  // The server's deriveTeamOverrides, case for case.
  const teamRules = (raw) => {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
    const teams = {}
    for (const rawSlug of Object.keys(raw)) {
      const slug = rawSlug.trim().toLowerCase()
      const rule = raw[rawSlug]
      if (!slug || !rule || typeof rule !== 'object' || Array.isArray(rule)) continue
      const r = {}
      if (declared(rule.read)) r.read = LEVEL_READ.includes(rule.read) ? rule.read : 'admin'
      if (declared(rule.write)) r.write = LEVEL_WRITE.includes(rule.write) ? rule.write : 'admin'
      if (Object.keys(r).length) teams[slug] = r
    }
    return Object.keys(teams).length ? teams : null
  }

  const out = []
  for (const c of list) {
    const key = c && typeof c.key === 'string' ? c.key.trim() : ''
    if (!key) continue
    const fields = fieldRules(c.fields)
    const teams = teamRules(c.teams)
    const raw = c.access
    // No declared access but the entry's OWN levels: kept as declared, never
    // widened to the plain key.
    if (!declared(raw) && (declared(c.read) || declared(c.write))) {
      const entry = { key }
      if (declared(c.read)) entry.read = LEVEL_READ.includes(c.read) ? c.read : 'admin'
      if (declared(c.write)) entry.write = LEVEL_WRITE.includes(c.write) ? c.write : 'admin'
      if (
        (entry.read === 'scoped' || entry.write === 'scoped') &&
        typeof c.scopeField === 'string' &&
        c.scopeField.trim()
      ) {
        entry.scopeField = c.scopeField.trim()
      }
      if (fields) entry.fields = fields
      if (teams) entry.teams = teams
      out.push(entry)
      continue
    }
    const levels =
      raw === undefined || raw === null || raw === 'own'
        ? null
        : raw === 'own-admin'
          ? { read: 'member', write: 'admin' }
          : raw === 'own-rows'
            ? { read: 'member', write: 'own' }
            : raw === 'private-rows'
              ? { read: 'own', write: 'own' }
              : { read: 'member', write: 'admin' }
    // A field-rule bag or a team map needs the OBJECT entry shape; the levels
    // are the ones the access maps to (a plain key's are member / editor).
    if (fields || teams) {
      out.push({
        key,
        ...(levels || { read: 'member', write: 'editor' }),
        ...(fields ? { fields } : {}),
        ...(teams ? { teams } : {})
      })
    } else out.push(levels ? { key, ...levels } : key)
  }
  return out
}
